import { post } from '@/api/client';
import { T } from '@/i18n';
import { Button } from '@/ui';
import { type FormEvent, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Field, KontoBox, failureCode, failureText, hint, passwordProblem, tokenFromQuery } from './shared';

type Refusal = 'invalid' | 'expired' | 'used';

/** The link from the recovery mail: POST /account/reset with the token in the query and the new password. */
export default function NyttPassord() {
  const token = tokenFromQuery(useLocation().search);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // a missing token is the invalid state, and no call is made
  const [refusal, setRefusal] = useState<Refusal | null>(token ? null : 'invalid');

  async function submit(e: FormEvent) {
    e.preventDefault();
    const pp = passwordProblem(password, confirm);
    if (pp) return setErr(pp);
    setBusy(true);
    setErr(null);
    try {
      await post('/account/reset', { token, password });
      setDone(true);
    } catch (ex) {
      const code = failureCode(ex);
      if (code === 'invalid' || code === 'expired' || code === 'used') setRefusal(code);
      else setErr(failureText(ex));
    } finally {
      setBusy(false);
    }
  }

  const askAgain = (
    <Link to="/glemt-passord" style={hint}>
      {T('Be om en ny lenke')}
    </Link>
  );
  return (
    <KontoBox title={T('Nytt passord')}>
      {done ? (
        <div className="opt" style={{ display: 'grid', gap: 10 }}>
          <output className="al info" style={{ margin: 0 }}>
            {T('Passordet er endret. Logg inn med det nye passordet.')}
          </output>
          <Link to="/login" style={hint}>
            {T('Logg inn')}
          </Link>
        </div>
      ) : refusal ? (
        <div className="opt" style={{ display: 'grid', gap: 10 }}>
          {refusal === 'invalid' && (
            <div className="al dgr" style={{ margin: 0 }}>
              {T('Lenken er ikke gyldig. Sjekk at hele adressen fra e-posten ble med.')}
            </div>
          )}
          {refusal === 'expired' && (
            <div className="al dgr" style={{ margin: 0 }}>
              {T('Lenken har utløpt. Den gjelder i 60 minutter.')}
            </div>
          )}
          {refusal === 'used' && (
            <div className="al dgr" style={{ margin: 0 }}>
              {T('Lenken er allerede brukt.')}
            </div>
          )}
          {askAgain}
        </div>
      ) : (
        <form className="opt" onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
          <Field
            label={T('Nytt passord (minst 8 tegn)')}
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <Field
            label={T('Gjenta passordet')}
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            last
          />
          {err && <div className="al dgr">{err}</div>}
          <Button tone="pri" type="submit" disabled={busy}>
            {busy ? '…' : T('Lagre passord')}
          </Button>
        </form>
      )}
    </KontoBox>
  );
}
